import React, { useState } from 'react';
import '../../scss/match/LineupListView.scss';
import logo from '../../images/logo-long.png';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';

// Import background images
import training1 from '../../images/bg/training1.jpg';
import training2 from '../../images/bg/training2.jpg';
import training3 from '../../images/bg/training3.jpg';
import training4 from '../../images/bg/training4.jpg';
import training5 from '../../images/bg/training5.jpg';
import training6 from '../../images/bg/training6.jpg';
import training7 from '../../images/bg/training7.jpg';
import training8 from '../../images/bg/training8.jpg';

const LineupListView = ({ teams, match, teamNames = {}, singleTeamMode = false, playerPool = [] }) => {
    console.log('LineupListView teams:', teams);
    const { white, colour } = teams || {};
    const [isCapturing, setIsCapturing] = useState(false);
    
    // Check for team logo
    const selectedTeamId = sessionStorage.getItem('selected_team_id');
    const [teamLogo, setTeamLogo] = useState(null);
    
    // Background image state
    const [currentBgImage, setCurrentBgImage] = useState('/static/media/lineup-bg.17a93386953825bc7d62.jpg');
    
    // Try to load team logo
    React.useEffect(() => {
        if (selectedTeamId) {
            try {
                const logoPath = require(`../../images/teams/${selectedTeamId}/logo-light.png`);
                setTeamLogo(logoPath);
            } catch (e) {
                console.log(`No team logo found for team ID: ${selectedTeamId}`);
                setTeamLogo(null);
            }
        }
    }, [selectedTeamId]);
    
    // Default team names if not provided
    const whiteTeamName = teamNames.white || 'White Team';
    const colourTeamName = teamNames.colour || 'Colour Team';
    
    const formatMatchTime = (timeString) => {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        return `${hour}${minutes !== '00' ? ':' + minutes : ''}${hour >= 12 ? 'pm' : 'am'}`;
    };

    // Helper function to format player names
    const formatPlayerName = (player, allPlayers) => {
        if (!player.name) return 'Unknown Player';
        
        const fullName = player.name;
        const lastName = fullName.split(' ').pop();
        
        // Check if there are any other players with the same last name
        const playersWithSameLastName = allPlayers.filter(p => 
            p.name && p.name.split(' ').pop() === lastName && p.id !== player.id
        );
        
        if (playersWithSameLastName.length > 0) {
            // If there are players with the same last name, show full name
            return fullName;
        }
        
        // Otherwise, show first initial and last name
        const firstName = fullName.split(' ')[0];
        return `${firstName[0]}. ${lastName}`;
    };

    const captureScreenshot = async () => {
        try {
            setIsCapturing(true);
            const modalElement = document.querySelector('.lineup-list-view');
            if (!modalElement) return;

            // Hide the capture button during screenshot
            const captureButton = modalElement.querySelector('.capture-button');
            if (captureButton) {
                captureButton.style.display = 'none';
            }



            const canvas = await html2canvas(modalElement, {
                backgroundColor: '#000000',
                scale: 4,
                logging: true,
                useCORS: true,
                allowTaint: true,
                onclone: (clone) => {
                    // Hide any branding logo that might exist
                    const logoContainer = clone.querySelector('.branding-logo');
                    if (logoContainer) {
                        logoContainer.style.display = 'none';
                    }
                    
                    // Hide the change image button during screenshot
                    const changeButton = clone.querySelector('.change-image-button');
                    if (changeButton) {
                        changeButton.style.display = 'none';
                    }
                }
            });

            // Show the capture button again
            if (captureButton) {
                captureButton.style.display = 'flex';
            }

            // Create download link
            const image = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = image;
            link.download = `lineup-${format(new Date(match.match_date), 'yyyy-MM-dd')}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Screenshot failed:', error);
        } finally {
            setIsCapturing(false);
        }
    };
    
    // Get all players for name formatting
    const allPlayers = [...(white || []), ...(colour || []), ...(playerPool || [])];
    
    // Calculate total players for conditional styling
    const totalPlayers = (white ? white.length : 0) + (colour ? colour.length : 0);
    const isCompactMode = totalPlayers > 11;
    
    // Function to change background image
    const changeBackgroundImage = () => {
        const backgroundImages = [
            '/static/media/lineup-bg.17a93386953825bc7d62.jpg', // Default background
            training1,
            training2,
            training3,
            training4,
            training5,
            training6,
            training7,
            training8
        ];
        
        const currentIndex = backgroundImages.indexOf(currentBgImage);
        const nextIndex = (currentIndex + 1) % backgroundImages.length;
        setCurrentBgImage(backgroundImages[nextIndex]);
    };
    
    return (
        <div className="lineup-list-view">
            <div className="lineup-background" style={{ backgroundImage: `url(${currentBgImage})` }} />
            <div className="lineup-content" style={{ position: 'relative' }}>
                <div className="lineup-header">
                    {teamLogo && (
                        <div className="team-logo" style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            top: '20px',
                            width: '60px',
                            height: '60px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <img src={teamLogo} alt="Team Logo" style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }} />
                        </div>
                    )}
                    <div className="match-title">
                        <h2>{sessionStorage.getItem('selected_team_name')}<br/>STARTING XI</h2>
                        {match && (
                            <>
                                <h3 className="location">{match.location}</h3>
                                <h3 className="date-time">
                                    {format(new Date(match.match_date), 'EEE d MMMM, yyyy')} {formatMatchTime(match.match_time)}
                                </h3>
                            </>
                        )}
                    </div>
                </div>
                
                <div className={`teams-container ${singleTeamMode ? 'single-team' : ''}`}>
                    <div className={`team-section white-team ${singleTeamMode ? 'full-width' : ''}`}>
                        <h3>{whiteTeamName.toUpperCase()}</h3>
                        <div className={`players-list ${isCompactMode ? 'compact' : ''}`}>
                            {white && white.length > 0 ? (
                                white.map((player, index) => (
                                    <div key={player.id || index} className="player-row">
                                        <span className="player-number">{player.number || '-'}</span>
                                        <span className="player-name">{formatPlayerName(player, allPlayers)}</span>
                                    </div>
                                ))
                            ) : (
                                <div className="no-players">No players assigned</div>
                            )}
                        </div>
                    </div>

                    {!singleTeamMode && (
                        <div className="team-section colour-team">
                            <h3>{colourTeamName.toUpperCase()}</h3>
                            <div className={`players-list ${isCompactMode ? 'compact' : ''}`}>
                                {colour && colour.length > 0 ? (
                                    colour.map((player, index) => (
                                        <div key={player.id || index} className="player-row">
                                            <span className="player-number">{player.number || '-'}</span>
                                            <span className="player-name">{formatPlayerName(player, allPlayers)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-players">No players assigned</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                
                {playerPool && playerPool.length > 0 && (
                    <div className="substitutes-section">
                        <h4>SUBSTITUTES</h4>
                        <div className="substitutes-list">
                            {playerPool.map((player, index) => (
                                <React.Fragment key={player.id || index}>
                                    <span className="sub-player">
                                        <span className="sub-player-number">{player.number || '-'}</span>
                                        {formatPlayerName(player, allPlayers)}
                                    </span>
                                    {index < playerPool.length - 1 ? ', ' : ''}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}

                <div className="button-container">
                    <button 
                        className={`capture-button ${isCapturing ? 'capturing' : ''}`}
                        onClick={captureScreenshot}
                        disabled={isCapturing}
                    >
                        <svg viewBox="0 0 24 24" width="24" height="24">
                            <path fill="currentColor" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                        </svg>
                        <span className="button-text">Download</span>
                        {isCapturing && <span className="capture-spinner" />}
                    </button>
                    
                    <button 
                        className="change-image-button"
                        onClick={changeBackgroundImage}
                    >
                        <svg viewBox="0 0 24 24" width="24" height="24">
                            <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                        </svg>
                        <span className="button-text">Change Image</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LineupListView; 